import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { usePage } from "@danishagro/shared/src/contexts/page.context";
import { getPage } from "@helpers/getPage.helper";
import { DA_Hero } from "@danishagro/shared/src/components/organisms/Hero/Hero.component";
import ComponentErrorBoundary from "@danishagro/shared/src/components/molecules/ErrorBoundary/ComponentErrorBoundary.component";
import { DA_NewPageData } from "@interfaces/newPageData.interface";
import { getAbsoluteUrl } from "@danishagro/shared/src/components/organisms/SiteHeader/LanguageSelector/hooks/getAbsolutePath.hook";
import { HeroObj } from "@danishagro/shared/src/interfaces/HeroObj.interface";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DynamicBlocksRenderer } from "@helpers/DynamicBlocksRenderer.helper";
import { useSchemaMarkup } from "@danishagro/shared/src/hooks/useSchemaMarkup.hook";
import { BlockData } from "@interfaces/blockData.interface";
import { SSH_HeroList } from "@danishagro/sharedShop/src/components/organisms/HeroList/HeroList.component";
import BlockRenderer from "@helpers/BlockRenderer";

const DA_HomePage = () => {
    const { setPageIsLoading } = usePage();
    const [pageData, setPageData] = useState<DA_NewPageData>();
    const [heroData, setHeroData] = useState<HeroObj>();
    const [heroImage, setHeroImage] = useState(null);
    const [heroVideo, setHeroVideo] = useState(null);
    const [heroButton, setHeroButton] = useState(null);
    const [heroPosterStart, setHeroPosterStart] = useState(null);
    const [heroBlocks, setHeroBlocks] = useState<BlockData[]>();
    const [isDynamicHero, setIsDynamicHero] = useState<boolean>(false);

    const { heroObj, customerNumber } = useAppData();

    // Use the custom hook for schema generation
    const dynamicSchemas = useSchemaMarkup(pageData);

    const getImageObject = (data: HeroObj) => {
        const image = data?.props?.image;

        if (!image || !image.props?.umbracoFile) {
            // Return null if the image or umbracoFile is missing
            return null;
        }

        if (image && image?.props?.umbracoFile?.includes("assetID")) {
            const imageWidth: number = 1400;
            const imageHeight: number = 600;
            const imageQuality: number = 80;

            const digizuiteSrc = image?.props?.umbracoFile
                .replace("{QUALITY}", `${imageQuality}`)
                .replace("{WIDTH}", `${imageWidth}`)
                .replace("{HEIGHT}", `${imageHeight}`);

            return {
                url: digizuiteSrc,
                width: imageWidth,
                height: imageHeight,
                alt: image.name,
            };
        } else if (image && image?.props?.umbracoFile) {
            return {
                url: getAbsoluteUrl(image?.props?.umbracoFile),
                width: image.props?.umbracoWidth,
                height: image.props?.umbracoHeight,
                alt: image.name,
            };
        } else {
            return null; // or handle the case where no image is available
        }
    };

    const getVideoObject = (data: HeroObj) => {
        const video = data?.props?.video;

        if (video && video.length > 0 && video[0]?.content?.props?.videoFile) {
            const videoContent = video[0].content;
            const videoProps = videoContent.props;
            const videoUrl = videoProps.videoFile.url;

            let posterStart = null;
            if (videoProps.posterStart) {
                const poster = videoProps.posterStart;
                posterStart = {
                    url: poster.url,
                    width: poster.props?.umbracoWidth
                        ? parseInt(poster.props.umbracoWidth)
                        : undefined,
                    height: poster.props?.umbracoHeight
                        ? parseInt(poster.props.umbracoHeight)
                        : undefined,
                    alt: poster.name,
                };
            }

            return {
                video: {
                    alias: videoContent.alias,
                    url: videoUrl,
                    autoplay: true,
                    controls: false,
                    muted: true,
                    loop: false,
                },
                posterStart: posterStart,
            };
        } else {
            return null;
        }
    };

    const setClassicHeroFromObj = (data: HeroObj) => {
        setHeroData(data);
        setHeroImage(getImageObject(data));
        // Handle both button structures: array or object with list
        const buttons = data?.props?.buttons;
        const buttonData =
            buttons && "list" in buttons
                ? buttons.list?.[0]?.content?.props
                : buttons?.[0]?.content?.props;
        setHeroButton(buttonData || null);
        const videoData = getVideoObject(data);
        if (videoData) {
            setHeroVideo(videoData.video);
            setHeroPosterStart(videoData.posterStart);
        } else {
            setHeroVideo(null);
            setHeroPosterStart(null);
        }
    };

    const heroImageUrl = (image: HeroObj["props"]["image"]): string | undefined => {
        const umbracoFile = image?.props?.umbracoFile;
        if (!umbracoFile) return undefined;

        if (umbracoFile.includes("assetID")) {
            const imageWidth: number = 1400;
            const imageHeight: number = 600;
            const imageQuality: number = 80;

            return umbracoFile
                .replace("{QUALITY}", `${imageQuality}`)
                .replace("{WIDTH}", `${imageWidth}`)
                .replace("{HEIGHT}", `${imageHeight}`);
        }

        return umbracoFile;
    };

    const heroObjToBlocks = (heroes: HeroObj[]): BlockData[] =>
        heroes.map((hero) => ({
            alias: "blockHero",
            key: hero.id,
            props: {
                ...hero.props,
                image: hero.props?.image
                    ? { ...hero.props.image, url: heroImageUrl(hero.props.image) }
                    : hero.props?.image,
            },
        }));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const applyHero = (heroArray: any) => {
        if (Array.isArray(heroArray) && heroArray.length > 0) {
            const firstHero = heroArray[0]?.content || {};
            const firstHeroType = firstHero.alias || "";
            const isDynamic = firstHeroType && firstHeroType !== "segmentHero";

            if (isDynamic) {
                // Dynamic multi-hero: extract hero blocks and render via HeroList
                const heroList = firstHeroType === "heroItem" ? firstHero.props?.heroes : [];
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                setHeroBlocks((heroList || []).map((item: any) => item.content));
                setIsDynamicHero(true);
                return;
            }
        }

        if (heroObj && heroObj.length > 1) {
            setHeroBlocks(heroObjToBlocks(heroObj));
            setIsDynamicHero(true);
            return;
        }

        setClassicHeroFromObj(heroObj?.[0]);
        setIsDynamicHero(false);
    };

    useEffect(() => {
        setPageIsLoading(true);

        getPage()
            .then((page: DA_NewPageData) => {
                // Prefer the hero array from the page data; fall back to the
                // injected globalThis.pageContent (matches B2C, and covers
                // localhost dev where getPage() fetches an API without heroes).
                const pageHero = page?.props?.hero;
                const heroArray =
                    Array.isArray(pageHero) && pageHero.length > 0
                        ? pageHero
                        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (globalThis as any).pageContent?.props?.hero;

                applyHero(heroArray);

                setPageData(page);
                setPageIsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching page content:", error);
                // On error fall back to the injected page content (B2C parity).
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const fallback = (globalThis as any).pageContent;
                applyHero(fallback?.props?.hero);
                if (fallback) {
                    setPageData(fallback);
                }
                setPageIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setPageIsLoading, customerNumber]);

    return (
        <>
            <Helmet>
                <title>{pageData?.name}</title>
                <meta name="description" content={pageData?.metaDescription} />
                {/* Dynamic Schema Markup from CMS */}
                {dynamicSchemas.map((schema, index) => (
                    <script key={index} type="application/ld+json">
                        {JSON.stringify(schema)}
                    </script>
                ))}
            </Helmet>

            <div className="frontpage">
                <div className="frontpage-content">
                    {/* "Classic" hero */}
                    {!isDynamicHero && heroData && (
                        <div className="frontpage-hero">
                            <ComponentErrorBoundary showNothing componentName="Hero">
                                <DA_Hero
                                    headline={heroData.props?.headline || ""}
                                    text={heroData.props?.text || ""}
                                    image={
                                        heroImage
                                            ? {
                                                  src: heroImage.url,
                                                  width: heroImage.width,
                                                  height: heroImage.height,
                                                  alt: heroImage.alt,
                                              }
                                            : undefined
                                    }
                                    video={heroVideo}
                                    posterStart={heroPosterStart}
                                    alias={heroData.alias}
                                    link={{
                                        title: heroButton?.label || heroButton?.link?.name || "",
                                        href: heroButton?.link?.url || "",
                                        target: heroButton?.link?.target || "_self",
                                        conventionalRouting: true,
                                    }}
                                    fullWidthMobile
                                    enableTracking={true}
                                />
                            </ComponentErrorBoundary>
                        </div>
                    )}

                    {isDynamicHero && heroBlocks && (
                        <SSH_HeroList blocks={heroBlocks} BlockRenderer={BlockRenderer} />
                    )}

                    {pageData?.props?.modules && (
                        <DynamicBlocksRenderer
                            blocks={pageData.props.modules.map((mod: any) => ({
                                ...mod.content,
                                version: mod.version,
                            }))}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default DA_HomePage;
